import React from 'react'
import {
  ariaAttr,
  isNumber,
  useCallbackRef,
  useEventListener,
  useMergeRefs,
  useUnmountEffect
} from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import type { SystemProps } from '@xstyled/styled-components'
import styled, { css, th } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'
import type { SizingData } from './utils'
import {
  calculateNodeHeight,
  getSizingData,
  removeHiddenTextarea
} from './utils'

export interface TextAreaProps
  extends Omit<
    HTMLXStyledProps<'textarea'>,
    'minH' | 'minHeight' | 'maxH' | 'maxHeight' | 'w'
  > {
  dynamicHeight?: boolean
  fullWidth?: boolean
  width?: SystemProps['w']
  invalid?: boolean
  rows?: number
  maxRows?: number
  minRows?: number
  cacheMeasurements?: boolean
  testId?: string
  onHeightChange?: (height: number, meta: { rowHeight: number }) => void
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
}

export const TextArea = forwardRef<TextAreaProps, 'textarea'>(
  (
    {
      cacheMeasurements,
      disabled = false,
      invalid = false,
      maxRows = 10,
      maxWidth = 800,
      minRows = 5,
      onChange: onChangeProp,
      onHeightChange: onHeightChangeProp,
      placeholder,
      readOnly = false,
      required = false,
      resize = 'none',
      rows,
      value,
      dynamicHeight = true,
      fullWidth = true,
      width: widthProp,
      ...rest
    },
    ref
  ) => {
    const [selfValue, setSelfValue] = React.useState<typeof value>(value || '')
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
    const heightRef = React.useRef<number>(0)
    const measurementsCacheRef = React.useRef<SizingData>()
    const refs = useMergeRefs(ref, textareaRef)
    const onChange = useCallbackRef(onChangeProp)
    const onHeightChange = useCallbackRef(onHeightChangeProp)
    const isControlled = React.useMemo(() => value !== undefined, [value])
    const width = React.useMemo(() => {
      if (fullWidth) return 'full'
      if (widthProp) return isNumber(widthProp) ? `${widthProp}px` : widthProp
      return 'initial'
    }, [fullWidth, widthProp])

    if (__DEV__ && rest.style) {
      if ('maxHeight' in rest.style) {
        console.warn(
          'Using `style.maxHeight` for <Textarea/> is not supported. Please use `maxRows`.'
        )
      }
      if ('minHeight' in rest.style) {
        console.warn(
          'Using `style.minHeight` for <Textarea/> is not supported. Please use `minRows`.'
        )
      }
    }

    const resizeTextarea = React.useCallback(() => {
      if (resize !== 'none' || !dynamicHeight) return

      const node = textareaRef.current
      const nodeSizingData =
        cacheMeasurements && measurementsCacheRef.current
          ? measurementsCacheRef.current
          : getSizingData(node as HTMLElement)

      if (!nodeSizingData) {
        return
      }

      measurementsCacheRef.current = nodeSizingData

      const [height, rowHeight] = calculateNodeHeight(
        nodeSizingData,
        node?.value || node?.placeholder || 'x',
        rows || minRows,
        rows || maxRows
      )

      if (heightRef.current !== height) {
        heightRef.current = height
        node?.style.setProperty('height', `${height}px`, 'important')
        onHeightChange?.(height, { rowHeight })
      }
    }, [
      resize,
      cacheMeasurements,
      rows,
      minRows,
      maxRows,
      onHeightChange,
      dynamicHeight
    ])

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizeTextarea()
        setSelfValue(event.target.value)
        onChange?.(event)
      },
      [resizeTextarea, onChange]
    )

    React.useLayoutEffect(resizeTextarea)
    useEventListener('resize', resizeTextarea, window, {
      enabled: resize === 'none' && dynamicHeight
    })
    useUnmountEffect(removeHiddenTextarea)

    React.useEffect(() => {
      if (isControlled) {
        setSelfValue((value as string) || '')
      }
    }, [value])

    const controlledValue = isControlled
      ? { value: selfValue }
      : { defaultValue: rest.defaultValue }

    return (
      <Root
        {...rest}
        {...controlledValue}
        w={width}
        ref={refs}
        placeholder={placeholder}
        onChange={handleChange}
        resize={resize}
        maxWidth={maxWidth}
        disabled={disabled}
        invalid={invalid}
        required={required}
        readOnly={readOnly}
        data-testid={rest.testId}
        aria-invalid={ariaAttr(invalid)}
        aria-required={ariaAttr(required)}
        aria-readonly={ariaAttr(readOnly)}
        aria-placeholder={placeholder || undefined}
        aria-multiline={true}
      />
    )
  }
)

if (__DEV__) {
  TextArea.displayName = 'TextArea'
}

const Root = styled.textareaBox`
  color: black;
  font-size: 15px;
  line-height: 120%;
  font-weight: normal;
  padding: 8px 16px;
  appearance: none;
  border-radius: 4px;
  background: ${th.color('white')};
  border: 1px solid ${th.color('grey.l2')};
  max-width: ${({ maxWidth }) =>
    isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};
  resize: ${({ resize }) => resize};
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &::placeholder {
    color: ${th.color('grey.d1')};
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:hover {
    border-color: ${th.color('grey.d2')};
  }

  &:active,
  &:focus {
    outline: none;
    border: 1px solid ${th.color('primary.base')};
    box-shadow: 0 0 0 1px ${th.color('primary.base')};
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;

      &:active,
      &:focus {
        border-color: ${th.color('grey.l2')};
        box-shadow: none;
      }
    `};

  ${({ invalid, disabled }) =>
    !disabled &&
    invalid &&
    css`
      border-color: error;

      &:hover {
        border-color: error;
      }

      &:active,
      &:focus {
        outline: none;
        border-color: error;
        box-shadow: 0 0 0 1px ${th.color('error')};
      }
    `};
`
